// Used in login-routing.module.ts

// Angular
import { Component, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";

// RXJS/Store
import * as RouterStore from "#core/router-store";
import { Store, select } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import * as fromStore from "../../store";

// Services/Types/Helpers
import { FormHelper } from "#core/helpers/form.helper";

// Validators
import { EmailValidator } from "#validators/email.validator";
import { Auth, RegistrationCheckRequest } from "@carnegie-learning/data-access-angular-passport";

@Component({
    selector: "app-login-registration",
    templateUrl: "./login-registration.component.html",
    styleUrls: ["./login-registration.component.scss"],
    standalone: false
})
export class LoginRegistrationComponent implements OnInit, OnDestroy {
    registrationForm = this.fb.group(
        {
            email: [
                "",
                {
                    validators: [Validators.required, EmailValidator.email],
                    updateOn: "change",
                },
            ],
            inviteHash: [
                "",
                {
                    validators: [Validators.required],
                    updateOn: "change",
                },
            ],
        },
        { updateOn: "change" }
    );
    maintenanceMode: boolean = (<any>window).maintenanceMode;
    bannerStatus$?: Observable<boolean>;
    bannerMessage$?: Observable<string>;
    bannerColor$?: Observable<string>;
    showProgressBar$?: Observable<boolean>;
    navLinks = [
        { label: "Login", path: "../form", id: "login-tab" },
        { label: "Register", path: "../register", id: "registration-tab" },
    ];
    private ngUnsubscribe = new Subject();

    constructor(
        private router: Router,
        public store: Store<fromStore.UserState>,
        private routerStore: Store<RouterStore.RouterStateUrl>,
        public auth: Auth,
        private fb: UntypedFormBuilder,
        private formHelper: FormHelper
    ) {}
    ngOnInit() {
        this.bannerStatus$ = this.store.pipe(
            select(fromStore.getRegisterBannerStatus)
        );
        this.bannerMessage$ = this.store.pipe(
            select(fromStore.getRegisterBannerMessage)
        );
        this.bannerColor$ = this.store.pipe(
            select(fromStore.getRegisterBannerColor)
        );
        this.showProgressBar$ = this.store.pipe(
            select(fromStore.getRegisterProgressBar)
        );

        if (this.maintenanceMode) {
            this.store.dispatch(fromStore.registerMaintenance());
        } else {
            this.store.dispatch(fromStore.registerResetState());
        }

        this.routerStore
            .pipe(select(RouterStore.getRouterState))
            .pipe(first())
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data) => {
                if (data && data.state.url.includes("register")) {
                    const inviteCode: string =
                        data.state.queryParams["invite-hash"];

                    // if code was passed, auto-fill
                    if (inviteCode) {
                        this.registrationForm.patchValue({
                            inviteHash: inviteCode,
                        });
                        this.registrationForm.get("inviteHash")?.disable();
                        this.registrationForm.markAsTouched();
                    }
                }
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    goToSupportForm() {
        window.open(
            "https://www.carnegielearning.com/lets-talk/#support",
            "_blank"
        );
    }

    validate(field: string, type: string) {
        return this.formHelper.Validate(this.registrationForm, field, type);
    }

    submit() {
        this.registrationForm.markAsUntouched();
        const payload: RegistrationCheckRequest = {
            email: this.registrationForm.get("email")?.value,
            inviteHash: this.registrationForm.get("inviteHash")?.value.trim(),
        };
        this.store.dispatch(
            fromStore.registerRegistrationCheckAttempt({
                payload: payload,
                email: this.registrationForm.get("email")?.value,
                inviteHash: this.registrationForm
                    .get("inviteHash")
                    ?.value.trim(),
            })
        );
    }
}
